import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { setLanguage } from '../../../../utils/i18-next';
import {
  countryDropdownListItems,
  mapCountryCodeToLanguageCode,
} from '../../../../utils/text/translation';
import { checkVat, isValidSwedishOrganisationNumber } from '../../../../utils/text/validation';
import { useOnValueChanged } from '../../../hooks/use-on-value-changed';
import { Button } from '../../button/button';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { Select } from '../../select/select';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';
import { Company, FormStateContext } from '../state/form-state-provider';
import { StepperContext } from '../state/stepper-provider';
import { isEmptyField, vatNumberOrOrgNumber } from '../utils';
import { Form, Grid } from './styles';

const CompanyInformationStep = () => {
  const { t } = useTranslation();
  const { company, setCompany } = React.useContext(FormStateContext);
  const { nextStep } = React.useContext(StepperContext);
  const {
    register,
    handleSubmit,
    watch,
    control,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<Company>({
    defaultValues: company,
  });

  useOnValueChanged(watch('countryCode'), () => {
    // When country is changed we want to revalidate org/vat field.
    void trigger('orgOrVatNumber');

    // And change language.
    void setLanguage(mapCountryCodeToLanguageCode(getValues('countryCode')));
  });

  return (
    <Form
      onSubmit={handleSubmit((data) => {
        // When form is valid, update state with form data.
        // Set orgOrVatNumber to the cleaned VAT if VAT was used. This means that user sees the
        // cleaned VAT when navigating back to this form. This is also the value that will be saved
        // to database.
        if (vatNumberOrOrgNumber(getValues('countryCode')) === 'vatNumber') {
          setCompany({
            ...data,
            orgOrVatNumber: checkVat(getValues('countryCode'), getValues('orgOrVatNumber')!)
              .cleanedVat,
          });
        } else {
          setCompany(data);
        }

        nextStep();
      })}
    >
      <Grid>
        <LabelledContainer
          required={true}
          text={t('registerCompany.step1.companyName', { ns: 'components' })}
        >
          {(formElementId) => (
            <TextBox
              {...register('name', {
                required: true,
                validate: (value) => !isEmptyField(value),
              })}
              errorMessage={errors.name?.message}
              id={formElementId()}
              maxLength={255}
            />
          )}
        </LabelledContainer>
        <LabelledContainer
          required={true}
          text={t('registerCompany.step1.country', { ns: 'components' })}
        >
          {(formElementId) => {
            const id = formElementId();
            return (
              <Controller
                control={control}
                defaultValue="SE"
                name="countryCode"
                render={({ field, formState }) => (
                  <Select
                    {...field}
                    id={id}
                    options={countryDropdownListItems.map((x) => ({
                      id: x.value,
                      name: `${x.flag} ${x.englishText} (${x.nativeText})`,
                    }))}
                  />
                )}
              />
            );
          }}
        </LabelledContainer>

        <LabelledContainer
          required={true}
          text={
            vatNumberOrOrgNumber(watch('countryCode')) === 'vatNumber'
              ? t('registerCompany.step1.vatNumber', { ns: 'components' })
              : t('registerCompany.step1.organisationNumber', { ns: 'components' })
          }
        >
          {(formElementId) => (
            <TextBox
              {...register('orgOrVatNumber', {
                required: true,

                validate: (value) => {
                  if (!value) {
                    return false;
                  }

                  // For Sweden, validate organisation number, otherwise validate VAT.
                  return getValues('countryCode') === 'SE'
                    ? isValidSwedishOrganisationNumber(value)
                    : checkVat(getValues('countryCode'), value).isValid;
                },
              })}
              errorMessage={errors.orgOrVatNumber?.message}
              id={formElementId()}
              maxLength={255}
            />
          )}
        </LabelledContainer>

        <LabelledContainer
          required={true}
          text={t('registerCompany.step1.address', { ns: 'components' })}
        >
          {(formElementId) => (
            <TextBox
              {...register('address', {
                required: true,
                validate: (value) => !isEmptyField(value),
              })}
              errorMessage={errors.address?.message}
              id={formElementId()}
              maxLength={255}
            />
          )}
        </LabelledContainer>
        <LabelledContainer
          required={true}
          text={t('registerCompany.step1.zipCode', { ns: 'components' })}
        >
          {(formElementId) => (
            <TextBox
              {...register('zipCode', {
                required: true,
                validate: (value) => !isEmptyField(value),
              })}
              errorMessage={errors.zipCode?.message}
              id={formElementId()}
              maxLength={255}
            />
          )}
        </LabelledContainer>
        <LabelledContainer
          required={true}
          text={t('registerCompany.step1.city', { ns: 'components' })}
        >
          {(formElementId) => (
            <TextBox
              {...register('city', {
                required: true,
                validate: (value) => !isEmptyField(value),
              })}
              errorMessage={errors.city?.message}
              id={formElementId()}
              maxLength={255}
            />
          )}
        </LabelledContainer>
      </Grid>

      <Stack
        alignItems="center"
        direction="row"
        justifyContent={'flex-end'}
        margin="1em 0 0 0"
        spacing={0.5}
      >
        <Button color="primary" type="submit" variant="contained">
          {t('next', { ns: 'common' })}
        </Button>
      </Stack>
    </Form>
  );
};

export { CompanyInformationStep };
